import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import UserCategoryModal from './UserCategoryModal';

const ALLOWED_TYPES = ["wc", "urinal", "shower", "faucet"];

// Un équipement n'est éligible à la répartition d'usagers que s'il a une
// formule de calcul par personne (compute_daily_usages côté back) ET une
// catégorie de taxonomie fiable (DIC_USAGE_TAXONOMY_BY_EQUIPMENT) — sinon
// l'usage créé aurait un volume nul et resterait "Non catégorisé". Exportée
// pour que la case à cocher manuelle de ClientUsages.js applique exactement
// la même règle que la sélection automatique par section/pièce ci-dessous.
export const isEquipmentAllowed = (equipment) => {
  if (!ALLOWED_TYPES.includes(equipment.type_equipment)) return false;
  if (equipment.type_equipment === "faucet") {
    const faucetType = equipment.technical_data?.faucet_type;
    const allowedFaucets = ["hand_wash", "changing_room"];
    return !faucetType || allowedFaucets.includes(faucetType);
  }
  return true;
};

// Vue d'ensemble de la répartition actuelle d'un usager (tous lots + usages
// orphelins confondus) : combien, et de quelle manière (par type de point d'eau,
// par section). Purement informatif — indépendant de la mécanique des lots.
function computeUsageOverview(buildingUsages, selectedUserId, equipments) {
  if (!selectedUserId) return { total: 0, byType: [], bySection: [] };

  const filtered = buildingUsages.filter(usage => usage.user_id === selectedUserId);
  // nb_users est stocké pour une pièce représentative : il compte autant de fois que la
  // pièce a d'exemplaires (nb_identical_rooms → location.room_multiplier), comme dans le
  // calcul du volume. Même règle côté API (audit_progress.py::_usage_overview_total).
  const roomMultiplier = {};
  (equipments || []).forEach(equipment => {
    roomMultiplier[equipment._id] = equipment.location?.room_multiplier || 1;
  });

  const typeMap = {};
  const sectionMap = {};

  filtered.forEach(usage => {
    const type = usage.equipment_type;
    const section = usage.name_section || 'Inconnu';
    const nbUsers = (usage.usage_data?.nb_users || 0) * (roomMultiplier[usage._id_equipement] || 1);
    const eqId = usage._id_equipement;

    if (!typeMap[type]) typeMap[type] = { type, total: 0, equipmentIds: new Set() };
    typeMap[type].total += nbUsers;
    typeMap[type].equipmentIds.add(eqId);

    if (!sectionMap[section]) sectionMap[section] = { section, byType: {} };
    if (!sectionMap[section].byType[type]) sectionMap[section].byType[type] = { type, total: 0, equipmentIds: new Set() };
    sectionMap[section].byType[type].total += nbUsers;
    sectionMap[section].byType[type].equipmentIds.add(eqId);
  });

  const finalizeTypes = (map) => Object.values(map)
    .map(t => ({ type: t.type, total: t.total, equipmentCount: t.equipmentIds.size }))
    .sort((a, b) => b.total - a.total);

  // "apply_on_each_type" applique le même nb_users indépendamment à chaque type
  // de point d'eau (mêmes personnes comptées une fois par type de facilité
  // utilisée). Sommer les totaux par type multiplierait artificiellement le
  // total par le nombre de types (souvent ×3) — le vrai total est le maximum
  // entre types, pas leur somme. Les totaux PAR type, eux, restent des sommes
  // légitimes (additifs entre lots/sections pour un même type).
  const byType = finalizeTypes(typeMap);
  const total = byType.length > 0 ? Math.max(...byType.map(t => t.total)) : 0;

  const bySection = Object.values(sectionMap)
    .map(s => {
      const typesForSection = finalizeTypes(s.byType);
      const sectionTotal = typesForSection.length > 0 ? Math.max(...typesForSection.map(t => t.total)) : 0;
      return { section: s.section, total: sectionTotal, byType: typesForSection };
    })
    .sort((a, b) => b.total - a.total);

  return { total, byType, bySection };
}

// Résumé lisible des zones d'un lot ("Bâtiment A (3 pièces), Accueil") pour la
// liste des lots — vide pour un lot créé uniquement par cases à cocher.
function describeLotZones(dist, sections, rooms) {
  const zoneIds = dist.scope?.selected_zone_ids || [];
  if (zoneIds.length === 0) return [];
  if (dist.scope?.assign_on === 'sections') {
    return sections.filter(s => zoneIds.includes(s._id)).map(s => s.name_section);
  }
  const roomCountBySection = {};
  rooms.filter(r => zoneIds.includes(r._id)).forEach(r => {
    roomCountBySection[r.id_section] = (roomCountBySection[r.id_section] || 0) + 1;
  });
  return sections
    .filter(s => roomCountBySection[s._id])
    .map(s => `${s.name_section} (${roomCountBySection[s._id]} pièce${roomCountBySection[s._id] > 1 ? 's' : ''})`);
}

export default function UserDistributionPanel({
  buildingId,
  buildingData,
  setBuildingData,
  buildingUsages,
  setBuildingUsages,
  equipments,
  sections,
  rooms,
  selectedEquipments,
  setSelectedEquipments,
  selectedAssignItems,
  setSelectedAssignItems,
  translate,
  onClose,
  onAssigned,
  onLotFormOpenChange,
}) {
  const [formData, setFormData] = useState({ nb_users: 0, assign_on: 'rooms', apply_on_each_type: true });
  const [saving, setSaving] = useState(false);
  const [editingUserCategory, setEditingUserCategory] = useState(null); // null | 'new' | building_user object
  const [distributions, setDistributions] = useState([]);
  const [loadingDistributions, setLoadingDistributions] = useState(true);
  const [editingDistributionId, setEditingDistributionId] = useState(null);
  const [view, setView] = useState('overview'); // 'overview' | 'lots'
  const [lotFormOpen, setLotFormOpen] = useState(false);
  const [openZoneSections, setOpenZoneSections] = useState([]);
  const [openOverviewSection, setOpenOverviewSection] = useState(null);

  const refreshDistributions = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/get_distributions_by_building/${buildingId}`);
      if (res.ok) setDistributions(await res.json());
    } catch (error) {
      console.error('Erreur lors du chargement des lots de répartition :', error);
    }
  };

  useEffect(() => {
    setLoadingDistributions(true);
    refreshDistributions().finally(() => setLoadingDistributions(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  // Les cases à cocher de la liste des points d'eau (ClientUsages.js) ne servent
  // qu'à ajuster le lot en cours : le parent ne les affiche que formulaire ouvert.
  useEffect(() => {
    onLotFormOpenChange?.(lotFormOpen);
  }, [lotFormOpen, onLotFormOpenChange]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Un input number renvoie toujours une string dans e.target.value ; on la
    // coerce ici pour que nb_users soit toujours un Number dans le payload envoyé
    // à /assign_users et /update_distribution (comme le font déjà les boutons +/-).
    let nextValue = value;
    if (type === 'checkbox') nextValue = checked;
    else if (type === 'number') nextValue = value === '' ? '' : Number(value);
    setFormData(prev => ({ ...prev, [name]: nextValue }));
  };

  // Coche / décoche des zones (sections ou pièces selon assign_on) et les points
  // d'eau éligibles qu'elles contiennent. Décocher retire tous les points d'eau
  // de la zone, y compris ceux cochés à la main dans la liste.
  const setZonesSelected = (zoneIds, select) => {
    const zoneIdSet = new Set(zoneIds);
    const equipmentIds = equipments
      .filter(equipment => zoneIdSet.has(formData.assign_on === 'rooms'
        ? equipment.location.room_id
        : equipment.location.section_id))
      .filter(equipment => !select || isEquipmentAllowed(equipment))
      .map(equipment => equipment._id);

    if (select) {
      setSelectedAssignItems(prev => [...new Set([...prev, ...zoneIds])]);
      setSelectedEquipments(prev => [...new Set([...prev, ...equipmentIds])]);
    } else {
      const equipmentIdSet = new Set(equipmentIds);
      setSelectedAssignItems(prev => prev.filter(id => !zoneIdSet.has(id)));
      setSelectedEquipments(prev => prev.filter(id => !equipmentIdSet.has(id)));
    }
  };

  const handleAssignItemClick = (id) => {
    setZonesSelected([id], !selectedAssignItems.includes(id));
  };

  const handleAssignOnChange = (value) => {
    setFormData(prev => ({ ...prev, assign_on: value }));
    setSelectedAssignItems([]);
  };

  const selectedEquipmentObjects = selectedEquipments
    .map(id => equipments.find(e => e._id === id))
    .filter(Boolean);

  const typeCounts = selectedEquipmentObjects.reduce((acc, eq) => {
    const type = eq.type_equipment;
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  // Seules les zones contenant au moins un point d'eau éligible sont proposées :
  // sélectionner une pièce technique ou une cuisine n'ajouterait rien au lot.
  const eligibleCounts = useMemo(() => {
    const byRoom = {};
    const bySection = {};
    equipments.filter(isEquipmentAllowed).forEach(equipment => {
      const { room_id, section_id } = equipment.location || {};
      byRoom[room_id] = (byRoom[room_id] || 0) + 1;
      bySection[section_id] = (bySection[section_id] || 0) + 1;
    });
    return { byRoom, bySection };
  }, [equipments]);

  const eligibleSections = useMemo(
    () => sections.filter(section => eligibleCounts.bySection[section._id]),
    [sections, eligibleCounts]
  );

  const roomsBySection = useMemo(() => {
    return sections.map(section => ({
      section,
      rooms: rooms.filter(room => room.id_section === section._id && eligibleCounts.byRoom[room._id]),
    })).filter(group => group.rooms.length > 0);
  }, [sections, rooms, eligibleCounts]);

  const hiddenRoomCount = rooms.length - roomsBySection.reduce((n, group) => n + group.rooms.length, 0);
  const hiddenSectionCount = sections.length - eligibleSections.length;
  const allZoneSectionsOpen = roomsBySection.length > 0
    && roomsBySection.every(group => openZoneSections.includes(group.section._id));

  const toggleZoneSection = (sectionId) => {
    setOpenZoneSections(prev => prev.includes(sectionId)
      ? prev.filter(id => id !== sectionId)
      : [...prev, sectionId]);
  };

  const userDistributions = useMemo(
    () => distributions.filter(d => d.module === 'users' && d.params?.user_id === formData.user_id),
    [distributions, formData.user_id]
  );

  const overview = useMemo(
    () => computeUsageOverview(buildingUsages, formData.user_id, equipments),
    [buildingUsages, formData.user_id, equipments]
  );

  const orphanUsages = useMemo(
    () => formData.user_id
      ? buildingUsages.filter(u => u.user_id === formData.user_id && !u.distribution_id)
      : [],
    [buildingUsages, formData.user_id]
  );

  // Budget = headcount of the selected user category. Chaque lot est indépendant
  // (pas de cumul entre lots, une zone peut volontairement en recouper une autre) :
  // on vérifie seulement que CE lot, à lui seul, ne dépasse pas le profil.
  const totalAvailable = formData.user_details?.number || 0;
  const exceedsBudget = !!formData.user_id && Number(formData.nb_users || 0) > totalAvailable;

  const resetLotForm = () => {
    setEditingDistributionId(null);
    setLotFormOpen(false);
    setOpenZoneSections([]);
    setFormData(prev => ({ ...prev, nb_users: 0, notes: '' }));
    setSelectedAssignItems([]);
    setSelectedEquipments([]);
  };

  const handleNewDistribution = () => {
    resetLotForm();
    setLotFormOpen(true);
  };

  const handleEditDistribution = (dist) => {
    const zoneIds = dist.scope.selected_zone_ids || [];
    setEditingDistributionId(dist._id);
    setLotFormOpen(true);
    setFormData(prev => ({
      ...prev,
      nb_users: dist.params.nb_users,
      apply_on_each_type: dist.params.apply_on_each_type,
      assign_on: dist.scope.assign_on || 'rooms',
      notes: dist.notes || '',
    }));
    setSelectedAssignItems(zoneIds);
    setSelectedEquipments(dist.scope.target_ids || []);
    // Déplie les sections où le lot a des pièces, les autres restent repliées.
    setOpenZoneSections([...new Set(rooms.filter(r => zoneIds.includes(r._id)).map(r => r.id_section))]);
  };

  const handleSelectUser = (u) => {
    resetLotForm();
    setView('overview');
    setOpenOverviewSection(null);
    setFormData(prev => ({ ...prev, user_id: u.user_id, user_details: u, nb_users: 0 }));
  };

  const handleToggleLotsView = () => {
    if (view === 'lots') {
      resetLotForm();
      setView('overview');
      return;
    }
    setView('lots');
    // Aucun lot encore : on ouvre directement le formulaire du premier.
    if (!loadingDistributions && userDistributions.length === 0) handleNewDistribution();
  };

  const handleDeleteDistribution = async (dist) => {
    const result = await Swal.fire({
      title: 'Supprimer ce lot ?',
      text: `Cela supprimera les ${dist.scope.target_ids?.length || 0} usages créés par cette répartition.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#d33',
      customClass: 'swal-custom',
    });
    if (!result.isConfirmed) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/delete_distribution/${dist._id}`, { method: 'DELETE' });
      if (!res.ok) {
        toast.error("Erreur lors de la suppression du lot.");
        return;
      }
      if (editingDistributionId === dist._id) resetLotForm();
      await refreshDistributions();
      await onAssigned?.();
      toast.success("Lot supprimé.");
    } catch (error) {
      toast.error("Erreur de connexion à l'API.");
    }
  };

  const handleDeleteUsagesForSelectedUser = async () => {
    if (!formData.user_id) return;

    const usagesToDelete = buildingUsages.filter(u => u.user_id === formData.user_id).map(u => u._id);
    if (usagesToDelete.length === 0) {
      toast.info("Aucun usage associé à cet usager.");
      return;
    }

    try {
      // /delete_usage_by_user (contrairement à /delete_usage_list) supprime aussi
      // les lots de répartition de cet usager, pour ne pas laisser de lots
      // orphelins pointant vers des usages qui n'existent plus.
      const response = await fetch(`${process.env.REACT_APP_API_URL}/delete_usage_by_user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ building_id: buildingId, user_id: formData.user_id }),
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error("Erreur : " + data.error);
        return;
      }
      setBuildingUsages(prev => prev.filter(u => !usagesToDelete.includes(u._id)));
      await refreshDistributions();
      resetLotForm();
      toast.success(`${data.deleted_count} usages et ${data.deleted_distribution_count} lot(s) supprimés.`);
    } catch (error) {
      toast.error("Erreur serveur : " + error.message);
    }
  };

  const handleSubmitDistribution = async () => {
    if (!formData.user_id) {
      Swal.fire({ title: 'Erreur', text: "Veuillez sélectionner une catégorie d'usager.", icon: 'error', customClass: 'swal-custom' });
      return;
    }
    if (!formData.nb_users || formData.nb_users <= 0) {
      Swal.fire({ title: 'Erreur', text: "Le nombre d'usagers doit être supérieur à 0.", icon: 'error', customClass: 'swal-custom' });
      return;
    }
    if (selectedEquipments.length === 0) {
      Swal.fire({ title: 'Erreur', text: "Veuillez sélectionner au moins un point d'eau.", icon: 'error', customClass: 'swal-custom' });
      return;
    }
    // Le dépassement du profil n'est qu'un avertissement (affiché dans le
    // formulaire) : on n'empêche pas l'enregistrement, une zone peut légitimement
    // dépasser le nombre "prévu" du profil.

    const isEditing = !!editingDistributionId;
    const payload = {
      building_id: buildingId,
      user_id: formData.user_id,
      user_details: formData.user_details,
      nb_users: formData.nb_users,
      apply_on_each_type: formData.apply_on_each_type,
      assign_on: formData.assign_on,
      selected_assign_items: selectedAssignItems,
      selected_equipments: selectedEquipments,
      notes: formData.notes || '',
    };

    setSaving(true);
    try {
      const url = isEditing
        ? `${process.env.REACT_APP_API_URL}/update_distribution/${editingDistributionId}`
        : `${process.env.REACT_APP_API_URL}/assign_users`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast.success(isEditing ? "Lot mis à jour avec succès !" : "Usagers répartis avec succès !");
        await refreshDistributions();
        await onAssigned?.();
        resetLotForm();
      } else {
        toast.error("Erreur lors de la répartition des usagers.");
      }
    } catch (error) {
      toast.error("Erreur de connexion à l'API.");
    } finally {
      setSaving(false);
    }
  };

  const handleUserCategorySaved = (updatedBuildingData, savedUser) => {
    setBuildingData(updatedBuildingData);
    if (formData.user_id && savedUser.user_id === formData.user_id) {
      setFormData(prev => ({ ...prev, user_details: savedUser }));
    }
  };

  const handleUserCategoryDeleted = async (updatedBuildingData, deletedUserId) => {
    setBuildingData(updatedBuildingData);
    // La catégorie supprimée a aussi supprimé ses usages/lots côté serveur —
    // rafraîchir les deux pour ne pas laisser l'UI référencer des données mortes.
    setBuildingUsages(prev => prev.filter(u => u.user_id !== deletedUserId));
    await refreshDistributions();
    await onAssigned?.();
    if (formData.user_id === deletedUserId) {
      resetLotForm();
      setView('overview');
      setOpenOverviewSection(null);
      setFormData({ nb_users: 0, assign_on: 'rooms', apply_on_each_type: true });
    }
  };

  const selectedUser = formData.user_id
    ? (buildingData.building_users || []).find(u => u.user_id === formData.user_id) || formData.user_details
    : null;

  const renderOverview = () => (
    <div className="cus-dist-view">
      <span className="cus-form-label">Répartition actuelle</span>

      {overview.total === 0 ? (
        <p className="cus-empty-state-text">
          Aucun usager réparti pour cette catégorie pour l'instant — cliquez sur « Modifier la répartition » pour créer un premier lot.
        </p>
      ) : (
        <>
          <div className="cus-dist-stat-grid">
            {overview.byType.map(stat => {
              const maxTotal = overview.byType[0]?.total || 1;
              const pct = maxTotal > 0 ? (stat.total / maxTotal) * 100 : 0;
              return (
                <div key={stat.type} className="cus-dist-stat-card">
                  <img src={`/icons/${stat.type}.png`} alt={stat.type} className="cus-dist-stat-icon" />
                  <span className="cus-dist-stat-number">{Math.round(stat.total)}</span>
                  <span className="cus-dist-stat-label">
                    {translate(stat.type)} · {stat.equipmentCount} pt{stat.equipmentCount > 1 ? 's' : ''}
                  </span>
                  <div className="cus-dist-budget-bar">
                    <div className="cus-dist-budget-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="cus-dist-section-list">
            {overview.bySection.map(sectionStat => {
              const isOpen = openOverviewSection === sectionStat.section;
              const maxSectionTotal = overview.bySection[0]?.total || 1;
              const pct = maxSectionTotal > 0 ? (sectionStat.total / maxSectionTotal) * 100 : 0;
              return (
                <div key={sectionStat.section} className="cus-dist-section-item">
                  <div className="cus-dist-section-header" onClick={() => setOpenOverviewSection(isOpen ? null : sectionStat.section)}>
                    <span className="cus-dist-section-name">{sectionStat.section}</span>
                    <span className="cus-dist-section-max">{Math.round(sectionStat.total)} usager{sectionStat.total > 1 ? 's' : ''}</span>
                  </div>
                  <div className="cus-dist-section-bar-track">
                    <div className="cus-dist-section-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  {isOpen && (
                    <div className="cus-dist-section-details">
                      <div className="cus-dist-stat-grid">
                        {sectionStat.byType.map(t => (
                          <div key={t.type} className="cus-dist-stat-card">
                            <img src={`/icons/${t.type}.png`} alt={t.type} className="cus-dist-stat-icon" />
                            <span className="cus-dist-stat-number">{Math.round(t.total)}</span>
                            <span className="cus-dist-stat-label">{translate(t.type)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  const renderZonePicker = () => {
    if (formData.assign_on === 'sections') {
      return (
        <div className="ceq-filter-chips">
          {eligibleSections.map(section => (
            <button key={section._id} type="button"
              className={`ceq-chip${selectedAssignItems.includes(section._id) ? ' ceq-chip--active' : ''}`}
              onClick={() => handleAssignItemClick(section._id)}>
              {section.name_section}
            </button>
          ))}
        </div>
      );
    }

    return (
      <div className="cus-dist-zone-list">
        {roomsBySection.map(({ section, rooms: sectionRooms }) => {
          const isOpen = openZoneSections.includes(section._id);
          const roomIds = sectionRooms.map(room => room._id);
          const selectedCount = roomIds.filter(id => selectedAssignItems.includes(id)).length;
          const allSelected = selectedCount === roomIds.length;
          return (
            <div key={section._id} className={`cus-dist-zone-section${selectedCount > 0 ? ' cus-dist-zone-section--has-selection' : ''}`}>
              <div className="cus-dist-zone-header" role="button" tabIndex={0}
                onClick={() => toggleZoneSection(section._id)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleZoneSection(section._id); } }}
                aria-expanded={isOpen}>
                <span className={`cus-dist-zone-chevron${isOpen ? ' cus-dist-zone-chevron--open' : ''}`}>▸</span>
                <input type="checkbox" className="cus-eq-select-checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = selectedCount > 0 && !allSelected; }}
                  onClick={e => e.stopPropagation()}
                  onChange={() => setZonesSelected(roomIds, !allSelected)}
                  aria-label={`Toute la section ${section.name_section}`}
                  title="Sélectionner / désélectionner toute la section" />
                <span className="cus-dist-zone-name">{section.name_section}</span>
                <span className="cus-dist-zone-count">
                  {selectedCount > 0 && <strong>{selectedCount} / </strong>}
                  {roomIds.length} pièce{roomIds.length > 1 ? 's' : ''}
                </span>
              </div>
              {isOpen && (
                <div className="cus-dist-zone-rooms ceq-filter-chips">
                  {sectionRooms.map(room => (
                    <button key={room._id} type="button"
                      className={`ceq-chip${selectedAssignItems.includes(room._id) ? ' ceq-chip--active' : ''}`}
                      onClick={() => handleAssignItemClick(room._id)}
                      title={room.nb_identical_rooms > 1
                        ? `Cette pièce représente ${room.nb_identical_rooms} pièces identiques : elle pèsera ${room.nb_identical_rooms}x plus dans la répartition.`
                        : undefined}>
                      {room.name_room}
                      {room.nb_identical_rooms > 1 && (
                        <span className="cus-room-multiplier-badge" style={{ marginLeft: 6 }}>
                          ×{room.nb_identical_rooms}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderLotForm = () => (
    <div className="cus-dist-lot-form">
      <div className="cus-dist-lot-form-title">
        {editingDistributionId ? 'Modifier ce lot' : 'Nouveau lot'}
      </div>

      <div className="cus-form-field cus-form-field--row">
        <label className="cus-form-label">Nombre à répartir</label>
        <div className="cus-stepper">
          <button type="button" className="cus-stepper-btn"
            onClick={() => setFormData(prev => ({ ...prev, nb_users: Math.max(0, Number(prev.nb_users || 0) - 1) }))}>−</button>
          <input type="number" name="nb_users" className="cus-stepper-input"
            value={formData.nb_users} min={0} step="any" onChange={handleFormChange} />
          <button type="button" className="cus-stepper-btn"
            onClick={() => setFormData(prev => ({ ...prev, nb_users: Number(prev.nb_users || 0) + 1 }))}>+</button>
        </div>
      </div>

      {exceedsBudget ? (
        <p className="cus-form-hint cus-form-hint--danger">
          ⚠ Ce lot ({formData.nb_users} usagers) dépasse le nombre prévu pour "{formData.user_details?.name}" ({totalAvailable}).
        </p>
      ) : (
        <p className="cus-form-hint">
          Nombre prévu pour "{formData.user_details?.name}" : {totalAvailable} usagers (chaque lot est indépendant, pas de cumul avec les autres lots).
        </p>
      )}

      <div className="cus-form-field cus-form-field--row">
        <label className="cus-form-label">Assigner par</label>
        <div className="cus-toggle-group">
          <button type="button" className={`cus-toggle-btn${formData.assign_on === 'sections' ? ' cus-toggle-btn--active' : ''}`}
            onClick={() => handleAssignOnChange('sections')}>Sections</button>
          <button type="button" className={`cus-toggle-btn${formData.assign_on === 'rooms' ? ' cus-toggle-btn--active' : ''}`}
            onClick={() => handleAssignOnChange('rooms')}>Pièces</button>
        </div>
      </div>

      <div className="cus-form-field">
        <div className="cus-dist-zone-toolbar">
          <label className="cus-form-label">
            Sélectionnez {formData.assign_on === "sections" ? "les sections" : "les pièces"}
          </label>
          {formData.assign_on === 'rooms' && roomsBySection.length > 1 && (
            <button type="button" className="cus-dist-link-btn"
              onClick={() => setOpenZoneSections(allZoneSectionsOpen ? [] : roomsBySection.map(g => g.section._id))}>
              {allZoneSectionsOpen ? 'Tout replier' : 'Tout déplier'}
            </button>
          )}
        </div>

        {renderZonePicker()}

        {(formData.assign_on === 'rooms' ? hiddenRoomCount : hiddenSectionCount) > 0 && (
          <p className="cus-form-hint">
            {formData.assign_on === 'rooms'
              ? `${hiddenRoomCount} pièce${hiddenRoomCount > 1 ? 's' : ''} sans sanitaire éligible masquée${hiddenRoomCount > 1 ? 's' : ''}.`
              : `${hiddenSectionCount} section${hiddenSectionCount > 1 ? 's' : ''} sans sanitaire éligible masquée${hiddenSectionCount > 1 ? 's' : ''}.`}
          </p>
        )}
      </div>

      <div className="cus-dist-total-banner">
        <span>{selectedEquipmentObjects.length} point{selectedEquipmentObjects.length > 1 ? 's' : ''} d'eau sélectionné{selectedEquipmentObjects.length > 1 ? 's' : ''}</span>
      </div>

      {Object.keys(typeCounts).length > 0 && (
        <div className="cus-dist-type-counts">
          {Object.entries(typeCounts).map(([type, count]) => (
            <span key={type} className="cus-dist-type-count-chip">{count} {translate(type)}</span>
          ))}
        </div>
      )}

      <p className="cus-form-hint">
        Ajustez la sélection directement dans la liste des points d'eau ci-dessous (case à cocher).
      </p>

      <div className="cus-form-field">
        <label className="cus-form-label">Hypothèses et sources</label>
        <textarea name="notes" className="cus-text-input" rows={2} value={formData.notes || ''}
          placeholder="Ex. : 12 salariés en open space d'après le plan d'occupation"
          onChange={handleFormChange} />
      </div>

      <div className="cus-dist-footer">
        <button type="button" className="ceq-reset-btn" onClick={resetLotForm} disabled={saving}>Annuler</button>
        <button type="button" className="cus-save-btn" onClick={handleSubmitDistribution}
          disabled={saving}
          title={exceedsBudget ? "Dépasse le nombre prévu du profil (autorisé, juste un avertissement)" : undefined}>
          {saving ? 'Enregistrement…' : editingDistributionId ? 'Mettre à jour ce lot' : 'Ajouter ces usagers'}
        </button>
      </div>
    </div>
  );

  const renderLots = () => (
    <div className="cus-dist-view">
      <div className="cus-dist-lots-header">
        <span className="cus-form-label">Lots de répartition</span>
        {!(lotFormOpen && !editingDistributionId) && (
          <button type="button" className="cus-dist-lot-btn" onClick={handleNewDistribution}>+ Nouveau lot</button>
        )}
      </div>

      {loadingDistributions ? (
        <p className="cus-form-hint">Chargement…</p>
      ) : userDistributions.length === 0 ? (
        <p className="cus-empty-state-text">Aucun lot pour cet usager pour l'instant.</p>
      ) : (
        <div className="cus-dist-lot-list">
          {userDistributions.map(dist => {
            const zones = describeLotZones(dist, sections, rooms);
            const nbTargets = dist.scope.target_ids?.length || 0;
            return (
              <div key={dist._id} className={`cus-dist-lot-row${editingDistributionId === dist._id ? ' cus-dist-lot-row--active' : ''}`}>
                <div className="cus-dist-lot-info">
                  <span className="cus-dist-lot-count">
                    {dist.params.nb_users} usagers
                    <span className="cus-dist-lot-meta"> · {nbTargets} point{nbTargets > 1 ? 's' : ''} d'eau</span>
                  </span>
                  {zones.length > 0 && (
                    <span className="cus-dist-lot-meta" title={zones.join(', ')}>
                      {zones.slice(0, 3).join(', ')}{zones.length > 3 ? `, +${zones.length - 3}` : ''}
                    </span>
                  )}
                  {dist.notes && (
                    <span className="cus-dist-lot-meta cus-dist-lot-notes" title={dist.notes}>📝 {dist.notes}</span>
                  )}
                </div>
                <div className="cus-dist-lot-actions">
                  <button type="button" className="cus-dist-lot-btn" onClick={() => handleEditDistribution(dist)}>Modifier</button>
                  <button type="button" className="cus-dist-lot-btn cus-dist-lot-btn--danger" onClick={() => handleDeleteDistribution(dist)}>Supprimer</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lotFormOpen && renderLotForm()}

      {orphanUsages.length > 0 && (
        <p className="cus-form-hint">
          {orphanUsages.length} usage{orphanUsages.length > 1 ? 's' : ''} existant{orphanUsages.length > 1 ? 's' : ''} sans lot de répartition (créés avant cette fonctionnalité).
        </p>
      )}

      {!lotFormOpen && (userDistributions.length > 0 || orphanUsages.length > 0) && (
        <button type="button" className="cus-dist-danger-btn" onClick={handleDeleteUsagesForSelectedUser}>
          Supprimer tous les usages de cet usager
        </button>
      )}
    </div>
  );

  return (
    <>
    <div className="cus-dist-panel">
      <div className="cus-dist-panel-header">
        <div className="ceq-filter-section-title">
          <span className="ceq-filter-accent-bar" />
          <span className="ceq-filter-title-text">Répartition des usagers</span>
        </div>
        <button type="button" className="ceq-modal-close" onClick={onClose} aria-label="Fermer">✕</button>
      </div>

      <div className="cus-dist-step">
        <div className="cus-dist-step-title">Sélectionnez une catégorie d'usager</div>

        <div className="cus-dist-user-grid">
          {(buildingData.building_users || []).map((u) => {
            const isSelected = formData.user_id === u.user_id;
            return (
              <div
                key={u.user_id}
                className={`cus-dist-user-card${isSelected ? ' cus-dist-user-card--active' : ''}`}
                onClick={() => { if (!isSelected) handleSelectUser(u); }}
              >
                <span className="cus-dist-user-name">{u.name}</span>
                <span className="cus-dist-user-count">{u.number} usagers</span>
              </div>
            );
          })}

          <div className="cus-dist-user-card cus-dist-user-card--add"
            onClick={() => setEditingUserCategory('new')}>
            <span className="cus-dist-user-add-icon">+</span>
            <span className="cus-dist-user-name">Ajouter une catégorie d'usager</span>
          </div>
        </div>

        {(buildingData.building_users || []).length === 0 && (
          <p className="cus-empty-state-text">Aucun usager défini pour ce bâtiment pour l'instant.</p>
        )}
      </div>

      {selectedUser && (
        <>
          <div className="cus-dist-user-actions">
            <div className="cus-dist-user-actions-summary">
              <span className="cus-dist-user-actions-name">{selectedUser.name}</span>
              <span className={`cus-dist-overview-total${overview.total > totalAvailable ? ' cus-dist-overview-total--over' : ''}`}>
                {Math.round(overview.total)}
                <span className="cus-dist-overview-total-sub"> / {totalAvailable} répartis</span>
              </span>
              <span className="cus-dist-user-actions-lots">
                · {userDistributions.length} lot{userDistributions.length > 1 ? 's' : ''}
              </span>
            </div>

            {overview.total > totalAvailable && (
              <p className="cus-form-hint cus-form-hint--danger">
                ⚠ Dépasse le nombre prévu pour "{selectedUser.name}" ({totalAvailable}).
              </p>
            )}

            <div className="cus-dist-user-actions-btns">
              <button type="button" className="cus-dist-action-btn"
                onClick={() => setEditingUserCategory(selectedUser)}>
                <span className="cus-dist-action-icon">✎</span>
                Modifier les caractéristiques
              </button>
              <button type="button"
                className={`cus-dist-action-btn cus-dist-action-btn--primary${view === 'lots' ? ' cus-dist-action-btn--active' : ''}`}
                onClick={handleToggleLotsView}>
                <span className="cus-dist-action-icon">{view === 'lots' ? '←' : '⇄'}</span>
                {view === 'lots' ? 'Retour à la vue d\'ensemble' : 'Modifier la répartition'}
              </button>
            </div>
          </div>

          {view === 'lots' ? renderLots() : renderOverview()}
        </>
      )}
    </div>

    {editingUserCategory && (
      <UserCategoryModal
        buildingId={buildingId}
        buildingData={buildingData}
        editingUser={editingUserCategory === 'new' ? null : editingUserCategory}
        onClose={() => setEditingUserCategory(null)}
        onSaved={handleUserCategorySaved}
        onDeleted={handleUserCategoryDeleted}
      />
    )}
    </>
  );
}
